import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import httpClient, { ApiResponse } from '../httpClient';
import { API_ENDPOINTS } from '../endpoints';
import { PaginatedResponse } from '../types';
import {
    CampaignSubmission,
    CampaignSubmissionListParams,
    ReprocessFailedSubmissionsResponse,
} from './types';

const CAMPAIGNS_KEY = 'campaigns';
const SUBMISSIONS_KEY = 'submissions';

export const useCampaignSubmissions = (
    campaignId: string,
    params?: CampaignSubmissionListParams
) => {
    return useQuery<PaginatedResponse<CampaignSubmission>>({
        queryKey: [CAMPAIGNS_KEY, campaignId, SUBMISSIONS_KEY, params],
        queryFn: async () => {
            const { data } = await httpClient.get<PaginatedResponse<CampaignSubmission>>(
                API_ENDPOINTS.CRM.CAMPAIGNS.SUBMISSIONS(campaignId),
                { params }
            );
            return data;
        },
        enabled: !!campaignId,
    });
};

export const useCampaignSubmission = (campaignId: string, submissionId: string) => {
    return useQuery<ApiResponse<CampaignSubmission>>({
        queryKey: [CAMPAIGNS_KEY, campaignId, SUBMISSIONS_KEY, submissionId],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<CampaignSubmission>>(
                API_ENDPOINTS.CRM.CAMPAIGNS.SUBMISSION_DETAILS(campaignId, submissionId)
            );
            return data;
        },
        enabled: !!campaignId && !!submissionId,
    });
};

export const useReprocessCampaignSubmission = () => {
    const queryClient = useQueryClient();

    return useMutation<CampaignSubmission, Error, { campaignId: string; submissionId: string }>({
        mutationFn: async ({ campaignId, submissionId }) => {
            const { data } = await httpClient.post<ApiResponse<CampaignSubmission>>(
                API_ENDPOINTS.CRM.CAMPAIGNS.SUBMISSION_REPROCESS(campaignId, submissionId)
            );
            return data.data;
        },
        onSuccess: (_, { campaignId }) => {
            queryClient.invalidateQueries({
                queryKey: [CAMPAIGNS_KEY, campaignId, SUBMISSIONS_KEY],
            });
            queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY, campaignId] });
        },
    });
};

export const useReprocessFailedCampaignSubmissions = () => {
    const queryClient = useQueryClient();

    return useMutation<ReprocessFailedSubmissionsResponse, Error, string>({
        mutationFn: async campaignId => {
            const { data } = await httpClient.post<ApiResponse<ReprocessFailedSubmissionsResponse>>(
                API_ENDPOINTS.CRM.CAMPAIGNS.SUBMISSIONS_REPROCESS_FAILED(campaignId)
            );
            return data.data;
        },
        onSuccess: (_, campaignId) => {
            queryClient.invalidateQueries({
                queryKey: [CAMPAIGNS_KEY, campaignId, SUBMISSIONS_KEY],
            });
            queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY, campaignId] });
        },
    });
};
